/**
 * Tenant API
 *
 * API functions for tenant CRUD operations, schema management,
 * and provisioning. All functions use authFetch for authenticated requests.
 *
 * @module features/tenants/api/tenantApi
 */

import type {
  Tenant,
  TenantListResponse,
  TenantStats,
  TenantFormData,
  TenantSchema,
  AvailableModel,
} from '../types';

const API_BASE = '/api/admin/tenants';

// Type for the authFetch function signature
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Structured error from tenant API calls.
 */
export interface TenantApiError {
  type: 'validation' | 'conflict' | 'general';
  status: number;
  message: string;
  fieldErrors: Record<string, string>;
}

/**
 * Parse a tenant API error response into a structured error.
 * Used by both create and edit flows for consistent field-level error display.
 */
export function parseTenantApiError(status: number, data: Record<string, unknown>): TenantApiError {
  const detail = (data.detail ?? '') as string | Array<{ loc: string[]; msg: string }>;

  // 409 — duplicate slug or conflict
  if (status === 409) {
    const msg = typeof detail === 'string' ? detail : 'A tenant with this identifier already exists';
    const fieldErrors: Record<string, string> = {};
    if (typeof detail === 'string' && detail.toLowerCase().includes('slug')) {
      fieldErrors.slug = 'This URL identifier is already in use';
    }
    return { type: 'conflict', status, message: msg, fieldErrors };
  }

  // 400 or 422 — validation errors (Pydantic)
  if (status === 400 || status === 422) {
    // Pydantic returns array of {loc, msg} objects
    if (Array.isArray(detail)) {
      const fieldErrors: Record<string, string> = {};
      detail.forEach((err: { loc: string[]; msg: string }) => {
        const field = err.loc[err.loc.length - 1];
        fieldErrors[field] = err.msg;
      });
      return {
        type: 'validation',
        status,
        message: Object.keys(fieldErrors).length > 0
          ? 'Please check the form for errors'
          : 'Validation error',
        fieldErrors,
      };
    }

    // String detail — try to route to specific field
    if (typeof detail === 'string') {
      const fieldErrors: Record<string, string> = {};
      const lower = detail.toLowerCase();
      if (lower.includes('isolation')) fieldErrors.isolation_level = detail;
      else if (lower.includes('email')) fieldErrors.admin_email = detail;
      else if (lower.includes('feature')) fieldErrors.features = detail;
      return {
        type: 'validation',
        status,
        message: detail || 'Invalid request. Please check your input.',
        fieldErrors,
      };
    }
  }

  // General error
  const msg = typeof detail === 'string' && detail
    ? detail
    : (data.message as string) || `Request failed (HTTP ${status})`;
  return { type: 'general', status, message: msg, fieldErrors: {} };
}

// ============================================================================
// Tenant CRUD Operations
// ============================================================================

/**
 * Fetch parameters for tenant list.
 */
export interface FetchTenantsParams {
  page?: number;
  pageSize?: number;
  status?: string;
  isolationLevel?: string;
  search?: string;
  sortBy?: string;
  sortDir?: string;
}

/**
 * Fetch paginated tenant list with filters.
 */
export const fetchTenants = async (
  authFetch: AuthFetch,
  params: FetchTenantsParams
): Promise<TenantListResponse> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.pageSize) queryParams.set('page_size', params.pageSize.toString());
  if (params.status && params.status !== 'all') queryParams.set('status', params.status);
  if (params.isolationLevel && params.isolationLevel !== 'all') {
    queryParams.set('isolation_level', params.isolationLevel);
  }
  if (params.search) queryParams.set('search', params.search);
  if (params.sortBy) queryParams.set('sort_by', params.sortBy);
  if (params.sortDir) queryParams.set('sort_dir', params.sortDir);
  // Include archived only when explicitly filtering for archived status
  if (params.status === 'archived') {
    queryParams.set('include_archived', 'true');
  }

  const response = await authFetch(`${API_BASE}?${queryParams.toString()}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to fetch tenants: ${response.statusText}`);
  }
  return response.json();
};

/** `GET /api/admin/tenants` accepts `page_size` up to 100 (`le=100`); larger values are a 422. */
export const TENANTS_MAX_PAGE_SIZE = 100;

/** Stop for a misbehaving `total_pages` (100 pages = 10,000 tenants). */
const FETCH_ALL_MAX_PAGES = 100;

/**
 * Fetch every tenant matching `params`, one page of TENANTS_MAX_PAGE_SIZE at a
 * time, until the response's `total_pages` is reached (WQ-347 / DEF-092: the
 * AI Allocations tab asked for 1,000 in one request and got a 422).
 */
export const fetchAllTenants = async (
  authFetch: AuthFetch,
  params: Omit<FetchTenantsParams, 'page' | 'pageSize'> = {},
  fetchPage: typeof fetchTenants = fetchTenants
): Promise<Tenant[]> => {
  const tenants: Tenant[] = [];
  for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page++) {
    const resp = await fetchPage(authFetch, { ...params, page, pageSize: TENANTS_MAX_PAGE_SIZE });
    tenants.push(...resp.tenants);
    if (page >= resp.total_pages || resp.tenants.length === 0) break;
  }
  return tenants;
};

/**
 * Fetch tenant statistics.
 */
export const fetchTenantStats = async (
  authFetch: AuthFetch
): Promise<TenantStats> => {
  const response = await authFetch(`${API_BASE}/stats`);
  if (!response.ok) {
    throw new Error(`Failed to fetch tenant stats: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch a single tenant by ID.
 */
export const fetchTenant = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<Tenant> => {
  const response = await authFetch(`${API_BASE}/${tenantId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch tenant: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Create a new tenant.
 */
export const createTenant = async (
  authFetch: AuthFetch,
  data: TenantFormData
): Promise<Tenant> => {
  const response = await authFetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      slug: data.slug,
      name: data.name,
      admin_email: data.admin_email,
      admin_name: data.admin_name || undefined,
      isolation_level: data.isolation_level,
      settings: data.settings,
      branding: data.branding,
      limits: data.limits,
      ai_config: data.ai_config,
      features: data.features.filter(f => f.trim()),
      vertical: data.vertical || undefined,
      navigation_overrides: data.navigation_overrides,
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // Preserve status for caller to handle specific error codes
    const err = new Error(error.detail || `Failed to create tenant: ${response.statusText}`);
    (err as Error & { status?: number; errorData?: unknown }).status = response.status;
    (err as Error & { status?: number; errorData?: unknown }).errorData = error;
    throw err;
  }
  return response.json();
};

/**
 * Update data for tenant update.
 */
export interface UpdateTenantData {
  name?: string;
  admin_email?: string;
  admin_name?: string;
  status?: string;
  settings?: TenantFormData['settings'];
  branding?: TenantFormData['branding'];
  limits?: TenantFormData['limits'];
  ai_config?: TenantFormData['ai_config'];
  features?: string[];
  vertical?: string;
  navigation_overrides?: TenantFormData['navigation_overrides'];
}

/**
 * Update an existing tenant.
 */
export const updateTenant = async (
  authFetch: AuthFetch,
  tenantId: string,
  data: UpdateTenantData
): Promise<Tenant> => {
  // Clean up data: filter empty features, remove undefined admin_name
  const cleanData = {
    ...data,
    admin_name: data.admin_name || undefined,
    features: data.features?.filter(f => f.trim()),
    vertical: data.vertical || undefined,
  };

  const response = await authFetch(`${API_BASE}/${tenantId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cleanData),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err = new Error(errorData.detail || `Failed to update tenant: ${response.statusText}`);
    (err as Error & { status?: number; errorData?: unknown }).status = response.status;
    (err as Error & { status?: number; errorData?: unknown }).errorData = errorData;
    throw err;
  }
  return response.json();
};

/**
 * Archive (soft-delete) a tenant.
 */
export const archiveTenant = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<void> => {
  const response = await authFetch(`${API_BASE}/${tenantId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to archive tenant: ${response.statusText}`);
  }
};

/**
 * Restore an archived tenant.
 */
export const restoreTenant = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<Tenant> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/restore`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to restore tenant: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Permanently delete an archived tenant.
 * Requires slug confirmation for safety.
 */
export const purgeTenant = async (
  authFetch: AuthFetch,
  tenantId: string,
  confirmSlug: string
): Promise<void> => {
  const params = new URLSearchParams({
    confirm: 'true',
    confirm_slug: confirmSlug,
  });
  const response = await authFetch(`${API_BASE}/${tenantId}/purge?${params.toString()}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to purge tenant: ${response.statusText}`);
  }
};

// ============================================================================
// Schema Operations
// ============================================================================

/**
 * Fetch all tenant schemas.
 */
export const fetchSchemas = async (
  authFetch: AuthFetch
): Promise<{ schemas: TenantSchema[] }> => {
  const response = await authFetch(`${API_BASE}/schemas/list`);
  if (!response.ok) {
    throw new Error(`Failed to fetch schemas: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Provision schema for a tenant.
 */
export const provisionSchema = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<{ schema_name: string }> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/provision-schema`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to provision schema: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Archive schema for a tenant.
 */
export const archiveSchema = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<void> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/archive-schema`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to archive schema: ${response.statusText}`);
  }
};

// ============================================================================
// Provisioning Operations
// ============================================================================

/**
 * Start tenant provisioning.
 */
export const startProvisioning = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<void> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/provision`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to start provisioning: ${response.statusText}`);
  }
};

/**
 * Provisioning status response.
 */
export interface ProvisioningStatusResponse {
  status: string;
  progress: number;
  step: string;
}

/**
 * Fetch tenant provisioning status.
 */
export const fetchProvisioningStatus = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<ProvisioningStatusResponse> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/provision/status`);
  if (!response.ok) {
    throw new Error(`Failed to fetch provisioning status: ${response.statusText}`);
  }
  return response.json();
};

// ============================================================================
// Models (LLM)
// ============================================================================

/**
 * Fetch available LLM models.
 */
export const fetchAvailableModels = async (
  authFetch: AuthFetch
): Promise<AvailableModel[]> => {
  const response = await authFetch('/api/admin/llm/models');
  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.statusText}`);
  }
  const data = await response.json();
  // API returns array directly, not wrapped in models property
  return Array.isArray(data) ? data : (data.models || []);
};
